import React, {useCallback, useState} from 'react';

import {
  ProgressData,
  ProgressActivity,
  ProgressStatus,
} from 'llm/deep-research/messages/DeepResearchProgress';
import {ToolDetail} from 'llm/deep-research/tool-details/ToolDetail';
import {ProgressToolCall} from 'llm/deep-research/tool-details/types';
import * as ModalContainer from 'ui-library/ModalContainer';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';

const ACTIVITY_MODAL_KEY = 'ACTIVITY_MODAL_KEY';

export interface ActivityModalControls {
  openActivityModal: (activityData: ProgressData, selectedToolId?: string) => void;
}

export function useActivityModalControls(): ActivityModalControls {
  const openActivityModal = useCallback((activityData: ProgressData, selectedToolId?: string) => {
    ModalContainer.open(
      ACTIVITY_MODAL_KEY,
      <ActivityModal
        activityData={activityData}
        initialSelectedToolId={selectedToolId}
        closeModal={() => {
          ModalContainer.close(ACTIVITY_MODAL_KEY);
        }}
      />
    );
  }, []);

  return {
    openActivityModal,
  };
}

export interface ActivityModalProps {
  activityData: ProgressData;
  closeModal: () => void;
  initialSelectedToolId?: string;
}

/**
 * Modal for displaying detailed progress activity for completed deep research.
 * Shows the full activity thread.
 */
export function ActivityModal({
  activityData,
  closeModal,
  initialSelectedToolId,
}: ActivityModalProps): JSX.Element {
  // Find initial tool if ID provided
  const initialTool = initialSelectedToolId
    ? activityData.historyThread.find((tool) => tool.toolId === initialSelectedToolId) || null
    : null;

  const [selectedTool, setSelectedTool] = useState<ProgressToolCall | null>(initialTool);

  const handleToolClick = useCallback((toolCall: ProgressToolCall) => {
    setSelectedTool(toolCall);
  }, []);

  const handleBackToActivity = useCallback(() => {
    setSelectedTool(null);
  }, []);

  const buttons: Array<ModalButton> = selectedTool
    ? [
        {
          text: 'Back',
          onClick: handleBackToActivity,
        },
        {
          text: 'Close',
          onClick: closeModal,
        },
      ]
    : [
        {
          text: 'Close',
          onClick: closeModal,
        },
      ];

  const title = selectedTool ? 'Tool Details' : 'Activity';

  return (
    <ExpandableModal title={title} onRequestClose={closeModal} buttons={buttons} closeableOnEscapeKey={true}>
      {selectedTool ? (
        <ToolDetail toolCall={selectedTool} />
      ) : (
        <ProgressActivity
          progressData={activityData}
          status={ProgressStatus.COMPLETED}
          onToolClick={handleToolClick}
        />
      )}
    </ExpandableModal>
  );
}
